import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Users, BedDouble, Bath, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useGuestyListings } from "@/components/LiveAvailability";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

const bookingRoute = (listing) => {
  const type = (listing.property_type || "").toLowerCase();
  if (type.includes("cabin")) return "/bet-cabin";
  if (type.includes("dome")) return "/domes-cabins#book";
  return "/glamping#book";
};

const typeLabel = (listing) => {
  const type = (listing.property_type || "").toLowerCase();
  if (type.includes("cabin")) return "Cabin";
  if (type.includes("dome")) return "Geodesic Dome";
  return "Safari Tent";
};

const ListingCard = ({ listing, index }) => (
  <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: index * 0.08 }}>
    <div
      data-testid={`live-listing-card-${listing.id}`}
      className="group border border-border/50 rounded-xl overflow-hidden bg-white card-lift h-full flex flex-col"
    >
      <Link to={bookingRoute(listing)} className="block relative aspect-[4/3] overflow-hidden">
        {listing.images?.[0] ? (
          <img
            src={listing.images[0]}
            alt={listing.title || listing.nickname}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-muted" />
        )}
        <Badge className="absolute top-3 left-3 bg-white/90 text-foreground font-body text-xs shadow-sm">
          {typeLabel(listing)}
        </Badge>
      </Link>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-2 line-clamp-1">
          {listing.title || listing.nickname}
        </h3>
        <div className="flex items-center gap-4 mb-4 font-body text-xs text-muted-foreground">
          {listing.accommodates != null && (
            <span className="flex items-center gap-1">
              <Users size={13} /> Sleeps {listing.accommodates}
            </span>
          )}
          {listing.bedrooms != null && listing.bedrooms > 0 && (
            <span className="flex items-center gap-1">
              <BedDouble size={13} /> {listing.bedrooms} BR
            </span>
          )}
          {listing.bathrooms != null && listing.bathrooms > 0 && (
            <span className="flex items-center gap-1">
              <Bath size={13} /> {listing.bathrooms} BA
            </span>
          )}
        </div>
        <div className="mt-auto flex items-center justify-between">
          <div>
            {listing.base_price != null && (
              <p className="font-heading text-xl font-semibold text-primary" data-testid={`live-listing-price-${listing.id}`}>
                ${listing.base_price}
                <span className="font-body text-xs text-muted-foreground font-normal"> / night from</span>
              </p>
            )}
          </div>
          <Link to={bookingRoute(listing)}>
            <Button
              data-testid={`live-listing-book-${listing.id}`}
              size="sm"
              className="bg-accent hover:bg-accent/90 text-white font-body text-xs rounded-full px-5"
            >
              Book Now <ArrowRight size={14} className="ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  </motion.div>
);

export const LiveListings = () => {
  const { listings, loading, error } = useGuestyListings(null);

  if (error) return null;
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="live-listings-loading">
        <Loader2 className="animate-spin text-primary" size={28} />
      </div>
    );
  }

  return (
    <div data-testid="live-listings-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {listings.map((listing, i) => (
        <ListingCard key={listing.id} listing={listing} index={i} />
      ))}
    </div>
  );
};
